import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserIdentity } from '../api/services/lupp/user-identity/dto.user-identity';
import type { Quote } from '../api/services/lupp/quotes/dto.quotes';

type Theme = 'dark' | 'light';

type SelectedProspect = {
  prospectIdentifier: string;
  prospectName: string;
  prospectEmail?: string;
} | null;

type Store = {
  theme: Theme;
  locale: string;
  isSidebarCollapsed: boolean;
  selectedProspect: SelectedProspect;
  selectedQuote: Quote | null;
  userIdentity: UserIdentity | null;
  toggleTheme: () => void;
  setLocale: (locale: string) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSelectedProspect: (prospect: SelectedProspect) => void;
  clearSelectedProspect: () => void;
  setSelectedQuote: (quote: Quote | null) => void;
  clearSelectedQuote: () => void;
  setUserIdentity: (identity: UserIdentity | null) => void;
  clearUserIdentity: () => void;
};

export const useStore = create<Store>()(
  persist(
    (set) => ({
      theme: 'dark',
      locale: 'en-us',
      isSidebarCollapsed: false,
      selectedProspect: null,
      selectedQuote: null,
      userIdentity: null,
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'dark' ? 'light' : 'dark',
        })),
      setLocale: (locale: string) =>
        set(() => ({
          locale,
        })),
      setSidebarCollapsed: (collapsed: boolean) =>
        set(() => ({
          isSidebarCollapsed: collapsed,
        })),
      setSelectedProspect: (prospect: SelectedProspect) =>
        set(() => ({
          selectedProspect: prospect,
        })),
      clearSelectedProspect: () =>
        set(() => ({
          selectedProspect: null,
        })),
      setSelectedQuote: (quote: Quote | null) =>
        set(() => ({
          selectedQuote: quote,
        })),
      clearSelectedQuote: () =>
        set(() => ({
          selectedQuote: null,
        })),
      setUserIdentity: (identity: UserIdentity | null) =>
        set(() => ({
          userIdentity: identity,
        })),
      clearUserIdentity: () =>
        set(() => ({
          userIdentity: null,
        })),
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({
        theme: state.theme,
        locale: state.locale,
        isSidebarCollapsed: state.isSidebarCollapsed,
        selectedProspect: state.selectedProspect,
        selectedQuote: state.selectedQuote,
        userIdentity: state.userIdentity,
      }),
    }
  )
);
